import { memo, useEffect, useState, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { sessionService } from 'redux-react-session';
import { Outlet, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom';
import _includes from 'lodash/includes';
// Components
import AppBar from 'components/AppBar/AppBar';
import PageLoader from 'components/Common/PageLoader/PageLoader';
import ComponentErrorWrapper from 'components/Common/ComponentErrorWraper/ComponentErrorWrapper';
import DemoBanner from 'components/Common/DemoBanner/DemoBanner';
import LocationsInspectionBanner from 'components/Common/LocationsInspectionBanner/LocationsInspectionBanner';
import DialogLoader from 'components/Common/DialogLoader/DialogLoader';
// Controllers
import CableController from 'components/Controllers/Cable/Cable';
import ConfirmationController from 'components/Controllers/Confirmation/Confirmation';
// Helpers
import { models, sides } from 'helpers/constants';
import { isIndexedDBError } from 'helpers';
// Hooks
import useCustomSelector from 'hooks/useCustomSelector';
import useError from 'hooks/useError';
import useRole from 'hooks/useRole';
import useSession from 'hooks/useSession';
import useUrl from 'hooks/useUrl';
import useProfile from 'hooks/useProfile';
import useOrders from 'hooks/useOrders';
import useSessions from 'hooks/useSessions';
import useAdds from 'hooks/useAdds';
import { getTowlotCatalog } from 'actions/towlotCatalog';

const { ORDERS, ACCOUNTS, TRUCKS, VEHICLES } = models;

// Dialogs -- all lazy-loaded: every one of these is always mounted here (closed by default) for the
// whole authenticated session, so none of them should sit in the main bundle just because they
// *might* be opened. MapDialog additionally pulls in mapbox-gl, the single biggest offender.
const Assign = lazy(() => import('components/Dialogs/Assign/Assign'));
const CancelOrder = lazy(() => import('components/Dialogs/CancelOrder/CancelOrder'));
const Confirmation = lazy(() => import('components/Dialogs/Confirmation/Confirmation'));
const FilePreview = lazy(() => import('components/Dialogs/FilePreview/FilePreview'));
const DocumentsDialog = lazy(() => import('components/Dialogs/Documents/Documents'));
const Invoice = lazy(() => import('components/Dialogs/Invoice/Invoice'));
const MapDialog = lazy(() => import('components/Dialogs/Map/Map'));
const RejectOrder = lazy(() => import('components/Dialogs/RejectOrder/RejectOrder'));
const Signature = lazy(() => import('components/Dialogs/Signature/Signature'));
const TowVehicle = lazy(() => import('components/Dialogs/TowVehicle/TowVehicle'));
const TakeOutVehicle = lazy(() => import('components/Dialogs/TakeOutVehicle/TakeOutVehicle'));
const VoiceCommand = lazy(() => import('components/Dialogs/VoiceCommand/VoiceCommand'));

const Layout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: paramsId, customer_token, company_id, order_id: orderId } = useParams();
  const { isAccountsRoute, isDriversRoute, isManagersRoute, isOrdersRoute, isInvoicesRoute, isTrucksRoute, isVehiclesRoute, isAuctionRoute } = useUrl();
  const { setError } = useError();
  const { getProfile } = useProfile();
  const { getOrder } = useOrders();
  const { createAdd } = useAdds();
  const { deleteActiveSession, clearStore } = useSessions();
  const profileFetched = useCustomSelector(state => !!state.profile.user.id);
  const isDemo = useCustomSelector(state => state.profile.user.demo);
  const { id: order_id } = useCustomSelector(state => state.invoices.invoice.order);
  const order = useCustomSelector(state => state.vehicles.vehicle.order);
  const towlotVehicle = useCustomSelector(state => state.towlotVehicles.towlotVehicle);
  const { authenticated, checked } = useSession();
  const { pathname, search } = useLocation();
  const { isManager, isOwner } = useRole();
  const [token, setToken] = useState(null);
  const redirectUrl = (isManager || isOwner) ? '/dashboard' : '/orders';

  const auctionOrderId =
    isAuctionRoute && paramsId && towlotVehicle &&
    String(towlotVehicle.id) === String(paramsId) && towlotVehicle.vehicle?.order?.id
      ? towlotVehicle.vehicle.order.id
      : null;

  const model =
    (isOrdersRoute || isInvoicesRoute || isVehiclesRoute) ? ORDERS
      : (isAuctionRoute && auctionOrderId) ? ORDERS
      : (isDriversRoute || isManagersRoute) ? 'users'
      : isAccountsRoute ? ACCOUNTS
      : isTrucksRoute ? TRUCKS
      : null;

  const id = isInvoicesRoute ? order_id
    : isVehiclesRoute ? order.id
    : isAuctionRoute ? auctionOrderId
    : paramsId;
  const isCustomer = !!customer_token || !!company_id;

  useEffect(() => {
    if (authenticated && checked && profileFetched && !isCustomer && (isManager || isOwner)) {
      dispatch(getTowlotCatalog());
    }
  }, [authenticated, checked, profileFetched, isCustomer, isManager, isOwner, dispatch]);

  useEffect(() => {
    const checkSession = () => sessionService.loadSession()
      .then(({ token: t }) => setToken(t))
      .catch((error) => {
        // Don't show IndexedDB errors to users - these are browser-level issues
        if (isIndexedDBError(error)) {
          return; // Silently handle IndexedDB errors
        }
        // Handle both string errors and Error objects
        const errorMessage = typeof error === 'string' ? error : (error?.message || '');
        if (!errorMessage.toLowerCase().includes('session not found')) {
          setError(error);
        }
      });

    if (authenticated && checked && !profileFetched) {
      if (!!!customer_token && !!!company_id) {
        getProfile()
        .then(() => {
          !token && checkSession();
          localStorage.removeItem('customer_token');
        })
        .catch(error => setError(error));
      } else {
        deleteActiveSession()
        .then(() => clearStore())
        .catch(error => setError(error));
      }
    }
  }, [authenticated, checked, token, customer_token, profileFetched, company_id, getProfile, setError, deleteActiveSession, clearStore]);
  useEffect(() => {
    if (checked && !authenticated && !!customer_token) {
      getOrder({ id: orderId, side: sides.CUSTOMER, customer_token })
      .then(() => { 
        localStorage.setItem('customer_token', customer_token);
        setToken(customer_token);
      })
      .catch(error => setError(error).then(() => {
        localStorage.removeItem('customer_token');
        navigate('/');
      }))
    }
  }, [orderId, getOrder, checked, authenticated, customer_token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      !!token && setToken(null);
    }
  }, [authenticated, checked, token]);
  useEffect(() => {
    if (checked && authenticated && _includes(pathname, 'add/redirect')) {
      const urlParams = new URLSearchParams(search);
      const code = urlParams.get('code');
      const state = new URLSearchParams(urlParams.get('state'));
      const company_id = state.get('company_id');
      const order_id = state.get('order_id');

      createAdd({ company_id, code })
      .then(({ payload: { status } }) => status === 204 ? navigate(`/orders/${order_id}`) : setError('Error with connection to ADD').then(() => navigate(`/`)))
      .catch((error) => setError(error))
    }
  }, [authenticated, checked, pathname, search, createAdd, setError]); // eslint-disable-line react-hooks/exhaustive-deps

  if (checked) {
    if (authenticated && !profileFetched) return <PageLoader />;
    if (pathname === '/' || (pathname === '/dashboard' && !isOwner && !isManager)) return <Navigate to={redirectUrl} replace />;
    return (
      <>
        { authenticated && !isCustomer && <>
        <ComponentErrorWrapper children={<AppBar />} />
        {isDemo && <DemoBanner />}
        <ComponentErrorWrapper children={<LocationsInspectionBanner />} />
        {/*
          Each dialog gets its own Suspense boundary rather than one shared boundary -- with a shared
          boundary, one dialog's chunk loading for the first time would fall back to DialogLoader for
          every dialog in the group, including any that are already open and rendering. DialogLoader
          (not null) so opening a dialog for the first time shows a brief spinner instead of looking
          like the click did nothing while its chunk loads.
        */}
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<Assign />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<CancelOrder />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<Confirmation />} /></Suspense>
        { !!model && <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<DocumentsDialog id={id} model={model} /> } /></Suspense> }
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<FilePreview />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<Invoice />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<MapDialog />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<RejectOrder />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<Signature />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<TowVehicle />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<TakeOutVehicle />} /></Suspense>
        <Suspense fallback={<DialogLoader />}><ComponentErrorWrapper children={<VoiceCommand />} /></Suspense>
        <ComponentErrorWrapper children={<ConfirmationController />} />
        </> }
        {token && <CableController token={token} />}
        <Outlet />
      </>
    );
  }
  return <PageLoader />;
};

export default memo(Layout);